import Layout from "../../components/layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import Accordion from "../../components/v2/accordion/accordion";
import Link from "../../components/Link";
import CallToActionSmall from "../../components/v2/call-to-action-sm";
import { GetStaticProps } from "next";
import ComparisonForm from "../../components/v2/form/comparison-form";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import dynamic from "next/dynamic";
import { useState } from "react";
import "../../styles/scss/pricing.module.scss";
import '../../styles/scss/_faq-section.module.scss';
import "../../styles/scss/solutions.module.scss";
import TrustedCompaniesV2 from "../../components/v2/trusted-companies-v2";
import VendorComparisonGrid from "../../components/v2/comparison/vendor-comparison-grid";
import VendorComparisonTable from "../../components/v2/comparison/vendor-comparison-table";
import {
    GRAFANA_COMPARISON_SECTIONS,
    GRAFANA_FEATURE_TABLE_ROWS,
    GRAFANA_SWITCH_STATS,
} from "../../components/v2/comparison/grafana-comparison-data";
import VendorSwitchStats from "../../components/v2/comparison/vendor-switch-stats";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import CardWithIcon from "../../components/v2/cards/card-with-icon";
import ComparisonVideoTestimonial from "../../components/v2/comparison/comparison-video-testimonial";
import { AIK8sRootIconDark, AutoFixIconDark, OpsAIDarkIcon, AIDashboardIconDark, AIAlertIconDark, ZeroReinstrumentationIcon, IncrementalAdoptionIcon, DataContinuityIcon } from "../../components/v2/icons-v2";

const Dialog = dynamic(() => import("../../components/v2/dialog"), {
    ssr: false,
});

export default function GrafanaVsMiddleware({ }) {
    const { t } = useTranslation('common');
    const data = t('comparison.grafana', { returnObjects: true, defaultValue: {} }) as any;

    const [testimonialVideoOpen, setTestimonialVideoOpen] = useState(false);
    const [testimonialVideoSrc, setTestimonialVideoSrc] = useState<string | null>(null);

    const handleTestimonialVideoOpen = (src: string) => {
        setTestimonialVideoSrc(src);
        setTestimonialVideoOpen(true);
    };

    const formData = [
        {
            id: 1,
            header: "Zero Re-instrumentation",
            icon: <ZeroReinstrumentationIcon />,

        },
        {
            id: 2,
            header: "Incremental Adoption",
            icon: <IncrementalAdoptionIcon />,

        },
        {
            id: 3,
            header: "100% Data Continuity",
            icon: <DataContinuityIcon />,

        },
    ]

    const faqs = [
        {
            id: 1,
            header: "What's the main difference between Middleware and Grafana Cloud's pricing models?",
            text: (
                <p>Middleware charges per GB of data ingested. Grafana Cloud bills across seven+ meters: dashboards per user, Mimir per 1K active series, Loki and Tempo per GB, k6 per virtual user hour, IRM per active IRM user, Assistant at $20/active AI user.</p>
            ),
        },
        {
            id: 2,
            header:
                "Does Middleware offer a free trial?",
            text: (
                <p>Yes — 14 days, unlimited data ingestion across every feature including OpsAI. No credit card required.</p>
            ),
        },
        {
            id: 3,
            header:
                "How is OpsAI different from Grafana Assistant?",
            text: (
                <p>Assistant is a copilot for dashboards and queries; Investigations are still in public preview. OpsAI is an SRE Agent — it investigates end-to-end, opens PRs with the code fix, and applies K8S remediation directly.</p>
            ),
        },
        {
            id: 4,
            header: "Can I keep using Grafana dashboards alongside Middleware?",
            text: (
                <p>Yes. OpsAI ingests Grafana alerts natively. Keep dashboards in Grafana, add the SRE Agent layer without a migration.</p>
            ),
        },
        {
            id: 5,
            header: "Can I save money by switching from Grafana Cloud?",
            text: (
                <p>Yes. Customers regularly see 30–50% reductions once Assistant, IRM, and Enterprise plugins are in the mix. Send us your invoice for a line-by-line model.</p>
            ),
        },
        {
            id: 6,
            header: "Is Middleware OpenTelemetry-native?",
            text: (
                <p>Yes. OTel is the foundation, not an afterthought. Your instrumentation stays portable.</p>
            ),
        },
    ];



    const softwareApplicationSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Middleware Observability Platform",
        "url": "https://middleware.io/",
        "applicationCategory": "DeveloperApplication",
        "operatingSystem": "Web",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.7",
            "reviewCount": "320"
        }
    };

    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": "https://middleware.io/comparison/grafana-vs-middleware/",
        "name": "Grafana vs Middleware – Observability Comparison",
        "description": "Compare Grafana and Middleware to understand differences in full-stack observability, ease of use, and pricing.",
        "mainEntity": {
            "@type": "ItemList",
            "itemListOrder": "Unordered",
            "numberOfItems": 2,
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "item": {
                        "@type": "SoftwareApplication",
                        "name": "Middleware Observability Platform",
                        "url": "https://middleware.io/",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web"
                    }
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                        "@type": "SoftwareApplication",
                        "name": "Grafana",
                        "url": "https://grafana.com/",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web"
                    }
                }
            ]
        },
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "url": "https://middleware.io/"
        }
    };


    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(softwareApplicationSchema)
                    }}
                />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(webPageSchema)
                    }}
                />
            </Head>
            <Layout
                isHomePage={false}
                pageTitle={"Grafana vs Middleware: Which Observability Platform Wins?"}
                ogImage={"/images/comparison/grafana-og.webp"}
                uri={String(data?.uri ?? "/comparison/grafana-vs-middleware/")}
                metaTitle={"Grafana vs Middleware: Which Observability Platform Wins?"}
                metaDesc={"Compare Grafana vs Middleware across dashboards, OpenTelemetry support, logs, metrics, traces, alerting, AI capabilities, pricing, and ease of use."}
                metaRobotsNoindex={String(data?.metaRobotsNoindex ?? "index")}
                metaRobotsNofollow={String(data?.metaRobotsNofollow ?? "follow")}
            >

                <div className={"mw-vs-datadog solutions"}>
                    <section className={`hero-section solutions-child-page`}>
                        <div className={'container'}>
                            <h1>The Better Grafana<span className='text-gradient'> Alternative</span><br /> Full-Stack Observability with an SRE Agent Built In</h1>
                            <p className="mb-4">Tired of four separately billed backends, seven overlapping meters, and a Grafana Assistant that writes queries but never ships the fix? Switch to Middleware, a unified, OpenTelemetry-native observability platform with predictable pricing, one-minute setup that comes with OpsAI, an SRE Agent that ships the fix as a pull request.</p>

                            <div className={"d-flex"}>
                                <Link href={"/demo-request/"} target={"_blank"}
                                    className={"sign-up ahref-btn primary"}>Request a Demo</Link>
                                <Link href={'#comparison-mw'} className={"sign-up ahref-btn secondary"}>See Comparison</Link>
                            </div>
                        </div>
                    </section>


                    <TrustedCompaniesV2
                        className="home-page-customer"
                        paragraph="Trusted By The Best Teams From"
                    />

                    <VendorComparisonGrid
                        sections={GRAFANA_COMPARISON_SECTIONS}
                        competitor={{
                            name: "Grafana",
                            logoSrc: "/images/comparison/grafana-border-logo.svg",
                            logoAlt: "Grafana",
                            cellLogoSrc: "/images/comparison/grafana-logo.svg",
                            headerLogoWidth: 140,
                            headerLogoHeight: 42,
                        }}
                        title={
                            <>
                                Why You <span className="text-gradient">Should Replace</span>{" "}
                                Grafana Cloud with <span className="text-gradient">Middleware</span>
                            </>
                        }
                    />

                    <CallToActionDocsLink
                        title={'Point your existing Grafana alerts at Middleware & switch incrementally'}
                        docsLink="https://docs.middleware.io"
                    />

                    <section className="ai-native-features">
                        <div className="container">
                            <h2>The first observability platform built around AI, <br />
                                not bolted onto it</h2>

                            <section className={'mw-card career'}>
                                <div className={'d-flex card-items'}>
                                    <CardWithIcon
                                        icon={<AIDashboardIconDark />}
                                        title={"AI Dashboards"}
                                        description={
                                            "Describe what you want in plain English. Middleware builds the dashboard, picks the charts, and writes the queries. No PromQL, no custom query language, no template gallery to comb through."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AIAlertIconDark />}
                                        title={"AI Alerts"}
                                        description={
                                            "Alerts that learn what \"normal\" looks like for each service and surface only the deviations that matter. Anomaly detection that doesn't drown your on-call rotation in noise."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<OpsAIDarkIcon />}
                                        title={"OpsAI · SRE Agent"}
                                        description={
                                            "An AI SRE that pinpoints root cause, identifies the offending line of code, and opens a pull request with the fix while you're still reading the alert. Auto-resolves issues with 80% accuracy."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AIK8sRootIconDark />}
                                        title={"AI K8s Root Cause Finder"}
                                        description={
                                            "Auto-structuring, severity detection, and pattern recognition built into log ingestion. No manual regular expressions, no parsing pipelines to maintain."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AutoFixIconDark />}
                                        title={"Auto-Fix · 60% Time Saved"}
                                        description={
                                            "Auto-fix across infrastructure and application layers with 80% accuracy. Engineers spend 60% less time on issue resolution — and the rest on shipping product."
                                        }
                                    />

                                </div>
                            </section>
                        </div>
                    </section>

                    <div className="customer-stories-comparison">
                        <div className="container">
                            <div className="section-tag testimonial">
                                Testimonials
                            </div>
                            <h2><span className="text-gradient">Customer </span> Stories</h2>


                            <div className="d-flex">
                                <div className="customer-story-card">
                                    <img src="/images/trusted-company/aceturtle.svg" alt="Aceturtle" />
                                    <p>Middleware has played a very good role in transforming our observability and application performance. For instance, we reduced our total observability costs by 50%.</p>
                                    <Link href="/customers/aceturtle/" className="read-more">
                                        Read the story
                                    </Link>
                                    <div className="customer-author">
                                        <p className="name">Vivek Gupta</p>
                                        <p>Founder & CEO</p>
                                    </div>
                                </div>

                                <div className="customer-story-card">
                                    <img src="/images/trusted-company/hotplate.svg" alt="Hotplate" />
                                    <p>We're heavy users of RUM and have tried many tools before discovering Middleware. We've been using OpsAI for the past few weeks, and it has automatically resolved about 60% of our production issues.</p>
                                    <Link href="/customers/hotplate/" className="read-more">
                                        Read the story
                                    </Link>
                                    <div className="customer-author">
                                        <p className="name">Rangaraj Tirumala</p>
                                        <p>Founding Engineer at Hotplate</p>
                                    </div>
                                </div>

                                <ComparisonVideoTestimonial onVideoOpen={handleTestimonialVideoOpen} />
                            </div>

                        </div>
                    </div>

                    <VendorComparisonTable
                        rows={GRAFANA_FEATURE_TABLE_ROWS}
                        competitor={{
                            name: "Grafana",
                            logoSrc: "/images/comparison/grafana-logo.svg",
                            logoAlt: "Grafana Logo",
                            cellLogoSrc: "/images/comparison/grafana-logo.svg",
                            cellLogoWidth: 90,
                            cellLogoHeight: 22,
                            headerLogoWidth: 140,
                            headerLogoHeight: 42,
                        }}
                    />

                    <VendorSwitchStats content={GRAFANA_SWITCH_STATS} />

                    <ComparisonForm title={"You Don't Have to Rip Out Grafana on Day One"} label={"Grafana"} formId={"0a5524e8-ff25-4db6-9634-8f9311b54673"} description={"Already running Grafana Alerts? Forward it to OpsAI and let it investigate & fix incidents against your existing stack without re-instrumentation"} isDatadog={true} formDataComparison={formData} docsLink="https://docs.middleware.io" />

                    <section className={"faq-section"}>
                        <div className={"container"}>
                            <h2>{String(data?.faq?.title ?? 'FAQs')}</h2>
                            <p>{String(data?.faq?.subtitle ?? '')}</p>
                            <div className={"faq-item"}>
                                <Accordion faqs={faqs} showGroups={true} />
                            </div>
                        </div>
                    </section>

                    <CallToActionSmall />

                </div>
            </Layout>

            <Dialog
                isOpen={testimonialVideoOpen}
                position="center"
                onClose={() => {
                    setTestimonialVideoOpen(false);
                    setTestimonialVideoSrc(null);
                }}
                size="md"
                verticalCenter={true}
            >
                <div style={{ textAlign: "center" }}>
                    {testimonialVideoSrc ? (
                        <video
                            key={testimonialVideoSrc}
                            playsInline
                            preload="none"
                            autoPlay
                            width="100%"
                            height={450}
                            controls={true}
                        >
                            <source src={testimonialVideoSrc} type="video/mp4" />
                        </video>
                    ) : null}
                </div>
            </Dialog>
        </>
    );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
    const translations = await serverSideTranslations(
        ctx?.locale ?? "en",
        ["common", "header", "footer"],
        nextI18NextConfig,
    );
    return {
        props: {
            ...translations,
            pageTitle: "Grafana vs Middleware: Which Observability Platform Wins?",
            description: "Compare Grafana vs Middleware across dashboards, OpenTelemetry support, logs, metrics, traces, alerting, AI capabilities, pricing, and ease of use.",
            ogImage: "/images/comparison/grafana-og.webp",
            uri: "/comparison/grafana-vs-middleware/",
            metaTitle: "Grafana vs Middleware: Which Observability Platform Wins?",
            metaDesc: "Compare Grafana vs Middleware across dashboards, OpenTelemetry support, logs, metrics, traces, alerting, AI capabilities, pricing, and ease of use.",
            metaRobotsNoindex: "index",
            metaRobotsNofollow: "follow",
        },
    }
}
